/**
 * Transport — the player chrome under the stage: the marker row, the scrubber
 * and the button bar. It owns no state of its own; every action is a call
 * into the `NotesSession` both columns share.
 */
import { useCallback, useState } from 'react';

import { Camera, CornersOut, Pause, Play, SkipBack, SkipForward } from '../../../../../design-system/icons';
import { cn } from '../../../../../lib/utils';
import { formatTimecode } from '../../../../../lib/media/timecode';
import { CAPTURE_UNAVAILABLE_TITLE, isCaptureUnsupported } from '../lib/captureAvailability';
import type { NotesSession } from '../session/useNotesSession';
import { MarkerRow } from './MarkerRow';
import { liveHoverOf, markerPreviewFor, type MarkerHover } from './markerPreview';
import { Track } from './Track';
import { useMediaTime } from './useMediaController';

const PLAY_ICON_PX = 18;
const STEP_ICON_PX = 16;
const EXPAND_ICON_PX = 17;
const CAPTURE_ICON_PX = 15;
/** Shown in place of the clock until `loadedmetadata` gives us a duration. */
const DURATION_UNKNOWN = '—';
const PLAY_LABEL = 'Play';
const PAUSE_LABEL = 'Pause';
const PLAY_PAUSE_TITLE = 'Play / pause (Space)';
const PREVIOUS_FRAME_LABEL = 'Previous frame';
const PREVIOUS_FRAME_TITLE = 'Previous frame (←)';
const NEXT_FRAME_LABEL = 'Next frame';
const NEXT_FRAME_TITLE = 'Next frame (→)';
export const FULL_PREVIEW_LABEL = 'Full preview';
const FULL_PREVIEW_TITLE = 'Full preview (F)';
const CAPTURE_LABEL = 'Capture frame';
const CAPTURE_ARIA_LABEL = 'Capture this frame';
const CAPTURE_TITLE = 'Capture frame (C)';

const TRANSPORT_BUTTON_CLASS =
  'inline-flex size-[32px] shrink-0 items-center justify-center rounded-full text-white/[.72] transition-colors duration-150 hover:bg-white/[.12] hover:text-white disabled:pointer-events-none disabled:opacity-40 motion-reduce:transition-none';
/**
 * Hover fills only while enabled; a disabled Capture keeps its pointer events
 * so its title can still say why it is unavailable.
 */
const CAPTURE_BUTTON_CLASS =
  'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border border-white/[.22] text-[12.5px] font-medium leading-none text-white transition-colors duration-150 motion-reduce:transition-none enabled:hover:border-white enabled:hover:bg-white enabled:hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-40';
const CAPTURE_PILL_CLASS = 'h-[30px] gap-[7px] px-[13px]';
/** A narrow column has no room for the label: a true circle, the size of the other transport buttons. */
const CAPTURE_CIRCLE_CLASS = 'size-[32px] justify-center p-0';

export interface TransportProps {
  session: NotesSession;
  /** The column is too narrow for the Capture label and the total time. */
  compact: boolean;
  src: string;
  crossOrigin?: 'anonymous';
}

function hasDuration(durationMs: number): boolean {
  return Number.isFinite(durationMs) && durationMs > 0;
}

/** Labelled by what the click will do. */
function playbackLabel(paused: boolean): string {
  return paused ? PLAY_LABEL : PAUSE_LABEL;
}

function PlaybackIcon({ paused }: { paused: boolean }) {
  if (paused) return <Play size={PLAY_ICON_PX} />;
  return <Pause size={PLAY_ICON_PX} />;
}

/** The same "why not" the composer's Capture tool gives, so the two never disagree. */
function captureTitle(session: NotesSession): string {
  if (isCaptureUnsupported({ ready: session.controller.ready, canCapture: session.canCapture })) return CAPTURE_UNAVAILABLE_TITLE;
  return CAPTURE_TITLE;
}

function captureShapeClass(compact: boolean): string {
  return compact ? CAPTURE_CIRCLE_CLASS : CAPTURE_PILL_CLASS;
}

function ClockReadout({ session, compact }: { session: NotesSession; compact: boolean }) {
  const { controller, fps } = session;
  const currentTimeMs = useMediaTime(controller);
  const className = 'ml-1.5 shrink-0 whitespace-nowrap text-[11.5px] tabular-nums text-white/[.55]';

  if (!hasDuration(controller.durationMs)) {
    return (
      <span className={className} data-testid="transport-clock">
        <b className="font-semibold text-white">{DURATION_UNKNOWN}</b>
      </span>
    );
  }

  // Frames only mean something on a still picture; while playing they churn.
  const nowOptions = controller.paused ? { fps, showFrames: true } : {};
  return (
    <span className={className} data-testid="transport-clock">
      <b className="font-semibold text-white">{formatTimecode(currentTimeMs, nowOptions)}</b>
      {!compact && <span> / {formatTimecode(controller.durationMs)}</span>}
    </span>
  );
}

export function Transport({ session, compact, src, crossOrigin }: TransportProps) {
  const { controller } = session;
  // Overlapping steps are already ignored by `session.frameStep`, which is
  // where every caller (buttons, slider keys, global ← / →) converges.
  const stepBack = useCallback(() => session.frameStep(-1), [session]);
  const stepForward = useCallback(() => session.frameStep(1), [session]);

  const togglePlayback = useCallback(() => {
    if (controller.paused) controller.play();
    else controller.pause();
  }, [controller]);

  const toggleFullView = useCallback(() => {
    session.setFullView((open) => !open);
  }, [session]);

  // The marker row and the rail share one preview, so the hovered marker lives here.
  const [markerHover, setMarkerHover] = useState<MarkerHover | null>(null);
  const hovered = liveHoverOf(markerHover, session.markers);

  return (
    <div className="px-[2px] pb-[2px] pt-3">
      <MarkerRow session={session} hovered={hovered} onHoverChange={setMarkerHover} />
      <Track session={session} src={src} crossOrigin={crossOrigin} markerPreview={markerPreviewFor(hovered, session.entryId)} />

      <div className="mt-1.5 flex flex-nowrap items-center gap-[2px]">
        <button
          type="button"
          aria-label={playbackLabel(controller.paused)}
          title={PLAY_PAUSE_TITLE}
          className={TRANSPORT_BUTTON_CLASS}
          onClick={togglePlayback}
        >
          <PlaybackIcon paused={controller.paused} />
        </button>
        <button
          type="button"
          aria-label={PREVIOUS_FRAME_LABEL}
          title={PREVIOUS_FRAME_TITLE}
          className={TRANSPORT_BUTTON_CLASS}
          disabled={!controller.ready}
          onClick={stepBack}
        >
          <SkipBack size={STEP_ICON_PX} />
        </button>
        <button
          type="button"
          aria-label={NEXT_FRAME_LABEL}
          title={NEXT_FRAME_TITLE}
          className={TRANSPORT_BUTTON_CLASS}
          disabled={!controller.ready}
          onClick={stepForward}
        >
          <SkipForward size={STEP_ICON_PX} />
        </button>

        <ClockReadout session={session} compact={compact} />

        <span className="flex-1" />

        <button type="button" aria-label={FULL_PREVIEW_LABEL} title={FULL_PREVIEW_TITLE} className={TRANSPORT_BUTTON_CLASS} onClick={toggleFullView}>
          <CornersOut size={EXPAND_ICON_PX} />
        </button>

        {/* The session owns the rule (`isCaptureAvailable`) so this button, the
            composer camera and the C shortcut can never disagree about it. */}
        <button
          type="button"
          aria-label={CAPTURE_ARIA_LABEL}
          title={captureTitle(session)}
          disabled={!session.canCaptureNow}
          onClick={session.requestCapture}
          className={cn(CAPTURE_BUTTON_CLASS, captureShapeClass(compact))}
        >
          <Camera size={CAPTURE_ICON_PX} className="shrink-0" />
          {!compact && <span>{CAPTURE_LABEL}</span>}
        </button>
      </div>
    </div>
  );
}
